import React, { useState, useEffect } from "react";
import {
  X,
  Users,
  UserPlus,
  Phone,
  Mail,
  Trash2,
  Loader2,
  ShieldCheck,
  Building,
} from "lucide-react";
import { showToast } from "../../components/common/notification/Notification";
import { fetchTeamMembers, deleteTeamMember } from "./networkApi";

const TeamDetailsDrawer = ({ show, team, onClose, onAddMember, onTeamDeleted }) => {
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingMember, setIsDeletingMember] = useState(null);

  const loadMembers = async () => {
    if (!team?.id) return;
    try {
      setIsLoading(true);
      const res = await fetchTeamMembers(team.id);
      setMembers(res.data || []);
    } catch (err) {
      console.error("Failed to load team members:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show && team?.id) {
      loadMembers();
    }
  }, [show, team?.id]);

  if (!show || !team) return null;

  const handleDeleteMember = async (member) => {
    const memberName = `${member.first_name || ""} ${member.last_name || ""}`.trim() || "member";
    if (!window.confirm(`Are you sure you want to remove ${memberName} from this team?`)) {
      return;
    }

    try {
      setIsDeletingMember(member.id);
      const res = await deleteTeamMember(member.id);
      if (res?.statusType === "SUCCESS" || res?.status === "SUCCESS") {
        showToast("Member removed from team", { type: "success" });
        loadMembers();
      } else {
        showToast(res?.text || "Failed to remove member", { type: "error" });
      }
    } catch (err) {
      console.error("Delete member error:", err);
      showToast("Error removing member", { type: "error" });
    } finally {
      setIsDeletingMember(null);
    }
  };

  const totalSeats = Number(team.total_seats) || 5;
  const usedSeats = members.length;
  const availableSeats = Math.max(0, totalSeats - usedSeats);
  const percentUsed = Math.min(100, Math.round((usedSeats / totalSeats) * 100));

  return (
    <div className="net-drawer-overlay" onClick={onClose}>
      <div className="net-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "520px" }}>
        <div className="net-drawer__header">
          <h3 className="net-drawer__title">Team Roster</h3>
          <button type="button" className="net-drawer__close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="net-drawer__body">
          {/* Team Overview Card */}
          <div
            style={{
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              borderRadius: "14px",
              padding: "1.25rem",
              marginBottom: "1.5rem",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
              <h2 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
                {team.name}
              </h2>
              <span
                style={{
                  background: team.status === "ACTIVE" ? "#f0fdf4" : "#f1f5f9",
                  color: team.status === "ACTIVE" ? "#16a34a" : "#64748b",
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  padding: "0.2rem 0.65rem",
                  borderRadius: "9999px",
                  border: `1px solid ${team.status === "ACTIVE" ? "#bbf7d0" : "#e2e8f0"}`,
                }}
              >
                {team.status || "ACTIVE"}
              </span>
            </div>

            {team.description && (
              <p style={{ fontSize: "0.8125rem", color: "#64748b", margin: "0 0 1rem 0", lineHeight: 1.4 }}>
                {team.description}
              </p>
            )}

            {/* Seat capacity bar */}
            <div style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#334155", display: "flex", justifyContent: "space-between" }}>
              <span>Capacity & Seats</span>
              <span>{usedSeats} / {totalSeats} seats filled</span>
            </div>

            <div className="net-team-seats-bar">
              <div
                className="net-team-seats-fill"
                style={{
                  width: `${percentUsed}%`,
                  background: percentUsed >= 90 ? "#ef4444" : "#2563eb",
                }}
              />
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.75rem", color: "#64748b" }}>
              <span>{availableSeats} seats remaining</span>
              <span>{percentUsed}% capacity</span>
            </div>
          </div>

          {/* Members List Header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
            <h4 className="net-drawer-section-title" style={{ margin: 0 }}>
              Team Members ({members.length})
            </h4>
            <button
              type="button"
              className="net-btn net-btn--primary net-btn--sm"
              onClick={() => onAddMember?.(team)}
            >
              <UserPlus size={14} /> Add Member
            </button>
          </div>

          {/* Member List */}
          {isLoading ? (
            <div className="net-loading-wrap" style={{ padding: "2rem 0" }}>
              <Loader2 size={24} className="net-spin" style={{ color: "#2563eb" }} />
              <p style={{ fontSize: "0.8125rem" }}>Loading team members...</p>
            </div>
          ) : members.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "2.5rem 1rem",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px dashed #cbd5e1",
              }}
            >
              <Users size={32} style={{ color: "#94a3b8", margin: "0 auto 0.5rem auto" }} />
              <p style={{ fontSize: "0.875rem", fontWeight: 600, color: "#0f172a", margin: "0 0 0.25rem 0" }}>
                No members in this team yet
              </p>
              <p style={{ fontSize: "0.75rem", color: "#64748b", margin: "0 0 1rem 0" }}>
                Add sales agents and associates to get started.
              </p>
              <button
                type="button"
                className="net-btn net-btn--primary net-btn--sm"
                onClick={() => onAddMember?.(team)}
              >
                <UserPlus size={14} /> Add First Member
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
              {members.map((m) => {
                const name = `${m.first_name || m.firstName || ""} ${m.last_name || m.lastName || ""}`.trim() || "Team Member";
                const phone = m.mobile_number || m.mobileNo || "";
                const email = m.email_address || m.emailId || "";
                const role = m.role || "MEMBER";

                return (
                  <div
                    key={m.id}
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "10px",
                      padding: "0.85rem 1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "0.75rem",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: 0 }}>
                      <div
                        style={{
                          width: "38px",
                          height: "38px",
                          borderRadius: "10px",
                          background: "#eff6ff",
                          color: "#1e40af",
                          fontSize: "0.875rem",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
                          <h5 style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", margin: 0 }}>
                            {name}
                          </h5>
                          <span
                            style={{
                              fontSize: "0.6875rem",
                              fontWeight: 700,
                              background: "#f1f5f9",
                              color: "#475569",
                              padding: "0.1rem 0.4rem",
                              borderRadius: "4px",
                            }}
                          >
                            {role}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.2rem" }}>
                          {phone && <span>+91 {phone.replace(/^91/, "")}</span>}
                          {email && <span> • {email}</span>}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="net-menu-trigger"
                      style={{ color: "#ef4444" }}
                      title="Remove member"
                      disabled={isDeletingMember === m.id}
                      onClick={() => handleDeleteMember(m)}
                    >
                      {isDeletingMember === m.id ? (
                        <Loader2 size={14} className="net-spin" />
                      ) : (
                        <Trash2 size={14} />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamDetailsDrawer;
